import { ReactElement, ReactNode, RefObject, forwardRef } from 'react'
import { useListBox } from '@react-aria/listbox'
import Styles from '../ListBox.module.scss'
import cx from 'classnames'
import { ListBoxBaseProps } from '../Types'
import { Option } from './Option'
import { Section } from './Section'
import { Virtualizer, VirtualizerItem } from '@react-aria/virtualizer'
import { Layout, ReusableView } from '@react-stately/virtualizer'
import { Collection, Node } from '@react-types/shared'
import { ListBoxContext } from '../ListBoxContext'

function VirtualizedListBoxBase<T extends object>(props: ListBoxBaseProps<T>, ref: RefObject<HTMLDivElement>) {
  const { layout, state, useFullWidth = false, wrapperClassName } = props
  const { listBoxProps } = useListBox(
    {
      ...props,
      keyboardDelegate: layout,
      isVirtualized: true,
    },
    state,
    ref,
  )

  type View = ReusableView<Node<{}>, ReactNode>
  let renderWrapper = (
    parent: View | null,
    reusableView: View,
    children: View[],
    renderChildren: (views: View[]) => ReactElement[],
  ) => {
    if (reusableView.viewType === 'section') {
      return (
        <Section
          key={reusableView.key}
          section={reusableView.content}
          layoutInfo={reusableView.layoutInfo}
          virtualizer={reusableView.virtualizer}
          headerLayoutInfo={children.find((c) => c.viewType === 'header')?.layoutInfo}
          state={state}
        >
          {renderChildren(children.filter((c) => c.viewType === 'item'))}
        </Section>
      )
    }

    if (!reusableView.layoutInfo) {
      return <div />
    }

    return (
      <VirtualizerItem
        key={reusableView.key}
        layoutInfo={reusableView.layoutInfo}
        virtualizer={reusableView.virtualizer}
        parent={parent?.layoutInfo ?? undefined}
      >
        {reusableView.rendered}
      </VirtualizerItem>
    )
  }

  return (
    <ListBoxContext.Provider value={state}>
      <Virtualizer
        {...listBoxProps}
        className={cx(Styles.root, { [Styles.fullWidth]: useFullWidth }, wrapperClassName)}
        ref={ref}
        sizeToFit="height"
        scrollDirection="vertical"
        layout={layout as Layout<Node<{}>>}
        collection={state.collection as Collection<Node<{}>>}
        renderWrapper={renderWrapper}
        focusedKey={state.selectionManager.focusedKey}
      >
        {(type, item: Node<{}>) => {
          if (type === 'item') {
            return <Option key={item.key} item={item} className={props.optionClassName} />
          } else {
            return null
          }
        }}
      </Virtualizer>
    </ListBoxContext.Provider>
  )
}

// forwardRef doesn't support generic parameters, so cast the result to the correct type
// https://stackoverflow.com/questions/58469229/react-with-typescript-generics-while-using-react-forwardref
// @ts-expect-error
const _VirtualizedListBoxBase = forwardRef(VirtualizedListBoxBase) as <T extends object>(
  props: ListBoxBaseProps<T> & { ref?: RefObject<HTMLDivElement> },
) => ReactElement
export { _VirtualizedListBoxBase as VirtualizedListBoxBase }
